import { ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import { useRef, useState, useEffect } from "react";

const faqs = [
  {
    question: "What is planniFi, and who is it designed for?",
    answer: "planniFi is a media planning, budgeting, and analysis software built for ecommerce brands and agencies to streamline campaign management and improve ad spend efficiency."
  },
  {
    question: "How are we different from other media planning tools?",
    answer: "We offer real-time analytics, cross-channel planning, intuitive dashboards, and seamless integrations, providing a more comprehensive and automated approach than traditional spreadsheets or manual tools."
  },
  {
    question: "Does planniFi support real-time analytics and reporting?",
    answer: "planniFi integrates with major platforms like Google Ads, Meta, TikTok, and Shopify."
  },
  {
    question: "What are the pricing plans?",
    answer: "We offer a single monthly plan at $247 per month or an annual plan at $2,388 billed annually ($199/month), which includes full access to the dashboard, API integrations, and reporting tools. At this time, there is a 7-day free trial available."
  },
  {
    question: "Which platforms does planniFi integrate with?",
    answer: "planniFi integrates with major ad platforms like Google Ads, Meta, TikTok, and Shopify."
  },
  {
    question: "How is my data used and stored?",
    answer: "User data is securely stored and used solely for providing insights and performance tracking within the platform. We do not sell or share user data with third parties."
  },
  {
    question: "What makes planniFi different from other reporting tools?",
    answer: "Most tools only show reporting. planniFi goes further by tracking budgets against goals, forecasting spend, and letting you plan campaigns on a calendar - so you can see how spend aligns with promotions."
  },
  {
    question: "Can I cancel my subscription at any time?",
    answer: "Yes, users can cancel their subscription at any time, and access will remain available until the end of the billing period."
  },
  {
    question: "How long for my data to populate after connecting APIs?",
    answer: "Once an API connection is established, data typically syncs within a few minutes, but in some cases, it may take up to 24 hours."
  },
  {
    question: "Will there be additional features or pricing plans in the future?",
    answer: "Yes, we plan to expand features and may introduce additional pricing tiers based on user needs."
  }
];

const FAQItem = ({ faq, index, rowIndex }: { faq: { question: string; answer: string }; index: number; rowIndex: number }) => {
  const [ref, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    triggerOnce: true
  });

  const [isInitialLoad] = useState(() => {
    return window.scrollY === 0 && rowIndex < 5;
  });

  return (
    <AccordionItem
      ref={ref}
      value={`item-${index}`}
      className={`rounded-lg px-6 bg-surface border-2 border-[#FAF9F8] text-deepocean data-[state=open]:bg-surface transition-all duration-500 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{
        transitionDelay: isInitialLoad ? `${rowIndex * 150}ms` : '50ms'
      }}
    >
      <AccordionTrigger className="hover:no-underline py-6">
        <span className="text-base md:text-lg font-medium text-left">{faq.question}</span>
      </AccordionTrigger>
      <AccordionContent className="text-deepocean/80 pb-6 font-charter text-base md:text-lg">
        {faq.answer}
      </AccordionContent>
    </AccordionItem>
  );
};

const FAQAccordion = () => {
  const midPoint = Math.ceil(faqs.length / 2);
  const leftColumnFaqs = faqs.slice(0, midPoint);
  const rightColumnFaqs = faqs.slice(midPoint);

  return (
    <section className="px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Accordion type="single" collapsible className="w-full space-y-6">
              {leftColumnFaqs.map((faq, index) => (
                <FAQItem key={index} faq={faq} index={index} rowIndex={index} />
              ))}
            </Accordion>
          </div>
          <div>
            <Accordion type="single" collapsible className="w-full space-y-6">
              {rightColumnFaqs.map((faq, index) => (
                <FAQItem 
                  key={index + midPoint} 
                  faq={faq} 
                  index={index + midPoint} 
                  rowIndex={index}
                />
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQAccordion;
